import React, {useState, useMemo} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useMoveFolderMutation} from '../../../data/documentWorkspaceApi';
import PropTypes from 'prop-types';
import {DARK_TEXT_COLOR, WHITE_100} from '../../../theme';
import {FOLDER_YEARS} from '../../../constants';

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
  },
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: WHITE_100,
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#229884',
    },
  },
  cancelButton: {
    borderRadius: '0.5rem',
    border: '1px solid #DDE0DE',
    background: WHITE_100,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '600',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
};

const MoveFolderModal = ({
  open,
  onClose,
  folder,
  onSuccess,
  onError,
  expandedYears,
  setExpandedYears,
}) => {
  const years = useMemo(
    () => FOLDER_YEARS.filter((year) => year !== folder.year),
    []
  );
  const [hoveredYear, setHoveredYear] = useState(null);
  const [moveFolder] = useMoveFolderMutation();

  const handleSubmit = async (targetYear) => {
    let result = null;
    if (folder && targetYear) {
      try {
        result = await moveFolder({
          folderId: folder.id,
          data: {name: folder?.name, year: targetYear},
        });

        if (result.error) {
          throw new Error(result.error);
        }

        onClose();
        if (onSuccess) {
          onSuccess(`Folder moved to ${targetYear}`);
          if (!expandedYears.includes(targetYear)) {
            setExpandedYears([...expandedYears, targetYear]);
          }
        }
      } catch (error) {
        console.error('Error moving folder:', error);
        if (onError) {
          onError(`Failed: ${result?.error?.data?.error}`);
        }
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'500px'} minWidth={'500px'} maxWidth={'500px'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={{...styles.modal.title, fontSize: '16px'}}>
                Move &ldquo;{folder?.name}&rdquo;
              </Typography>
              <IconButton size={'small'} onClick={onClose}>
                <CloseIcon size={'1.25rem'} />
              </IconButton>
            </Stack>

            <Divider orientation={'horizontal'} sx={styles.modal.divider} />

            <Stack direction={'column'} padding={'1.5rem'} spacing={'1rem'}>
              <Typography sx={styles.modal.title}>
                Current year: {folder?.year}
              </Typography>
              <Stack direction="column" spacing={2}>
                {years.map((year) => {
                  const isHovered = hoveredYear === year;

                  return (
                    <Box
                      key={year}
                      onMouseEnter={() => setHoveredYear(year)}
                      onMouseLeave={() => setHoveredYear(null)}
                      onClick={() => handleSubmit(year)}
                      sx={{
                        height: '4rem',
                        border: '1px solid #DFE5E2',
                        borderRadius: '8px',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#000000',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: DARK_TEXT_COLOR,
                        }}
                      >
                        {year}
                      </Typography>

                      {isHovered && (
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleSubmit(year)}
                          sx={{
                            color: '#26A993',
                            fontSize: '0.875rem',
                            fontWeight: '400',
                            textTransform: 'none',
                            padding: '4px 8px',
                            minWidth: 'auto',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: '#229884',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Move
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default MoveFolderModal;

MoveFolderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  folder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    year: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  expandedYears: PropTypes.array,
  setExpandedYears: PropTypes.func,
};
